import defaults from 'lodash/defaults';
import getSplashApiClient from 'lib/SplashApiClient/singleton';
import qs from 'qs';
import { camelizeKeys } from 'humps';

const DEFAULT_EVENT_SORT = 'modified_desc';
const DEFAULT_EVENT_LIMIT = 20;

const DEFAULT_EVENT_TYPE_LIMIT = 5;
const DEFAULT_EVENT_LOCATION_LIMIT = 5;
const DEFAULT_EVENT_TAG_LIMIT = 5;

export const VIEW_GROUP_BASIC = 'eventList';
export const VIEW_GROUP_DETAILS = 'eventDetail';
export const VIEW_GROUP_EXCLUDE_TICKETS = 'excludeTicketTypes';
const VIEW_GROUP_WORKFLOWS = 'eventWorkflow';

/**
 * Wraps a SplashApiClient with the Events Dashboard API actions
 */
export default class EventsApiClient {
    /**
     * @param {SplashApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Get events based on passed criteria
     * @param  {object} options
     * @param  {string} options.sort - What field to sort on
     * @param  {number} options.limit - Max # of results
     * @param  {string} options.search - Term for filtering results
     * @param  {number} options.page - Page to fetch based on limit
     * @param  {string} options.event_types - Event type id
     * @return {promise}
     */
    fetchEvents(options) {
        const fetchOptions = defaults(options, {
            sort: DEFAULT_EVENT_SORT,
            limit: DEFAULT_EVENT_LIMIT,
            viewGroups: [VIEW_GROUP_EXCLUDE_TICKETS, VIEW_GROUP_WORKFLOWS],
            include_themes: 0,
        });

        if (fetchOptions.search === '') {
            delete fetchOptions.search;
        }

        return this.apiClient.get('/events', fetchOptions);
    }

    /**
     * [searchEvents description]
     * @param  {string} search - Search term
     * @param  {object} options - Same as fetchEvents method
     * @return {promise}
     */
    searchEvents(search, options) {
        const searchOptions = {
            ...options,
            search,
            limit: null,
        };

        return this.fetchEvents(searchOptions);
    }

    /**
     * Get event types
     * @param  {string} options.sort - What field to sort on
     * @param  {number} options.limit - Max # of results
     * @param  {number} options.page - Page to fetch based on limit
     */
    fetchEventTypes(options) {
        const fetchOptions = defaults(options, {
            limit: DEFAULT_EVENT_TYPE_LIMIT,
        });

        return this.apiClient.get('/events/types', fetchOptions);
    }

    /**
     * Get event locations
     * @param  {string} options.sort - What field to sort on
     * @param  {number} options.limit - Max # of results
     * @param  {number} options.page - Page to fetch based on limit
     */
    fetchEventLocations(options) {
        const fetchOptions = defaults(options, {
            limit: DEFAULT_EVENT_LOCATION_LIMIT,
        });

        return this.apiClient.get('/events/locations', fetchOptions);
    }

    /**
     * Get event tags
     * @param  {string} options.sort - What field to sort on
     * @param  {number} options.limit - Max # of results
     * @param  {number} options.page - Page to fetch based on limit
     */
    fetchEventTags(options) {
        const fetchOptions = defaults(options, {
            limit: DEFAULT_EVENT_TAG_LIMIT,
        });

        return this.apiClient.get('/events/tags', fetchOptions);
    }

    uploadTempCsvFile(eventId, file) {
        const url = `/event/${eventId}/csv/upload/temp`;
        return this.apiClient.postFile(url, file);
    }

    /**
     * Batch delete events
     * @param  {number[]} ids
     * @return {promise}
     */
    deleteEvents(ids) {
        if (!Array.isArray(ids)) {
            throw Error('Delete Events: event ids must be in an array');
        }

        return this.apiClient.delete('/events', { ids });
    }

    /**
     *
     * @param action
     * @param ids
     * @returns {Promise}
     */
    publishUnpublishEvents(action, ids) {
        if (!Array.isArray(ids)) {
            throw Error('Publish/Unpublish Events: event ids must be in an array');
        }

        if (['publish', 'unpublish'].indexOf(action) === -1) {
            throw Error('Publish/Unpublish Events: action must be "publish" or "unpublish"');
        }

        const url = `/events/${action}`;

        return this.apiClient
            .put(url, { ids })
            .then((response) => ({
                ...response,
                data: camelizeKeys(response.data),
            }))
            .catch((response) =>
                Promise.reject({
                    ...response,
                    data: camelizeKeys(response.data),
                }),
            );
    }

    /**
     * get event details
     * @param {integer} eventId
     */
    getEventDetails(eventId) {
        return this.apiClient.get(`/events/${eventId}`);
    }

    /**
     * Gets the event's overview data
     * @param  {string | number} eventId
     * @return {Promise}
     */
    getEventOverview(eventId) {
        return this.apiClient.get(`/events/${eventId}/overview`);
    }

    /**
     * Gets the event's properties
     * @param  {string | number} eventId
     * @return {Promise}
     */
    getEventProperties(eventId) {
        return this.apiClient.get(`/events/${eventId}/properties`);
    }

    /**
     * Gets the event's attendee counts
     * @param  {string | number} eventId
     * @return {Promise}
     *
     * Example Response as of 2019-03-11:
     * {
     *       "meta": {
     *           "code": 200
     *       },
     *       "data": [
     *           {
     *               "name": "added",
     *               "count": 1
     *           },
     *           {
     *               "name": "invited",
     *               "count": 0
     *           },
     *           {
     *               "name": "waitlisted",
     *               "count": 0
     *           },
     *           {
     *               "name": "rsvp_no",
     *               "count": 0
     *           },
     *           {
     *               "name": "rsvp_yes",
     *               "count": 105
     *           },
     *           {
     *               "name": "checkin_yes",
     *               "count": 0
     *           },
     *           {
     *               "name": "checkin_no",
     *               "count": 0
     *           }
     *       ],
     *       "success": true
     *   }
     */
    getEventAttendeeCounts(eventId, urlPrefix = '') {
        return this.apiClient.get(`${urlPrefix}/events/${eventId}/guestlist/status/counts`);
    }

    /**
     * Get requested org properties
     * @param {string} propertyCodes
     * @return {Promise}
     */
    getOrgProperties({ code }) {
        const options = {};
        // if provided code has length greater than 0; then add to options
        // API controller has a default code if none provided
        if (code && code.length > 0) {
            options.code = code;
        }
        return this.apiClient.get('/organization/properties', options);
    }

    /**
     * Sends an export request to the API. On success, the logged in user is emailed a csv file
     * @param   {array<string>}  columns
     *
     * @return  {Promise}
     */
    exportEvents({ columns = [], filters }) {
        const columnObject = columns.reduce((acc, curr) => ({ ...acc, [curr]: curr }), {});
        return this.apiClient.post('/events/export', {
            columns: columnObject,
            filters,
        });
    }

    /**
     * get Events by organization Id
     * @param  {string | number} orgId
     *
     * @return  {Promise}
     */
    fetchEventsByOrgId(orgId, filtration) {
        const queryString = qs.stringify(filtration);

        return this.apiClient.get(`/admin/events/${orgId}?${queryString}`);
    }

    retriggerEvents(payload) {
        return this.apiClient.post('/integrations/sync', payload);
    }

    retriggerSalesforceEvents(payload) {
        return this.apiClient.post('/integrations/bulkSyncSalesforce', payload);
    }

    fetchEventFilterOptions(path, options = {}) {
        return this.apiClient.get(path, options);
    }

    fetchPlusOneCount(eventId) {
        return this.apiClient.get(`/events/${eventId}/guestlist/plus-one-count`);
    }

    changeGuestStatus(eventId, data) {
        const requestUrl = `/events/${eventId}/guestlist/status`;

        return this.apiClient.put(requestUrl, data);
    }

    getEventLists(eventId, options = {}) {
        const fetchOptions = defaults(options, {
            limit: 100,
            sort: 'name_asc',
        });

        return this.apiClient.get(`/event/${eventId}/lists`, fetchOptions);
    }

    createEventList(eventId, name) {
        return this.apiClient.post(`/event/${eventId}/lists`, { names: [name] });
    }

    getEventListsWithCount(eventId, options = {}, urlPrefix = '') {
        const fetchOptions = defaults(options, {
            limit: 100,
        });

        return this.apiClient.get(`${urlPrefix}/event/${eventId}/lists/counts`, fetchOptions);
    }

    updateEventList(eventId, listId, name) {
        return this.apiClient.put(`/event/${eventId}/list/${listId}`, { name });
    }

    deleteEventList(eventId, listId, data) {
        return this.apiClient.delete(`/event/${eventId}/list/${listId}`, data);
    }

    addGuestToList(eventId, listId, data) {
        return this.apiClient.post(`/event/${eventId}/list/${listId}/group-contacts`, data);
    }

    // Adds individual guests to an event list
    addGuestToEventList(eventId, guestId, data) {
        return this.apiClient.post(`/event/${eventId}/list/group-contact/${guestId}`, data);
    }

    // Removes individual guest from an event list
    removeGuestFromEventList(eventId, listId, data) {
        return this.apiClient.delete(`/event/${eventId}/list/${listId}/group-contacts`, data);
    }

    addGuestTag(eventId, data) {
        return this.apiClient.post(`/events/${eventId}/guestlist/tag`, data);
    }

    addGuestsToEmailCampaign(messageId, data) {
        return this.apiClient.post(`/events/${messageId}/guestlist/recipients`, data);
    }

    checkUserCanSendEmail(eventId) {
        return this.apiClient.get(`/event/${eventId}/message/can-send`);
    }

    checkEmailCredit(eventId) {
        return this.apiClient.get(`/event/${eventId}/email-credit/available`);
    }

    getSharedGuestList(eventId) {
        return this.apiClient.get(`/events/${eventId}/share-guest-list`);
    }

    createSharedGuestList(eventId, data) {
        return this.apiClient.post(`/events/${eventId}/share-guest-list`, data);
    }

    updateSharedGuestList(eventId, shareLinkId, data) {
        return this.apiClient.put(`/events/${eventId}/share-guest-list/${shareLinkId}`, data);
    }

    getEventForms(eventId) {
        return this.apiClient.get(`/events/${eventId}/forms`);
    }

    createEventForm(eventId, data) {
        return this.apiClient.post(`/events/${eventId}/forms`, data);
    }

    deleteEventForm(eventId, formId) {
        return this.apiClient.delete(`/events/${eventId}/forms/${formId}`);
    }

    updateEventForms(eventId, data) {
        return this.apiClient.put(`/events/${eventId}/forms`, data);
    }
}

export const getEventsApiClient = () => new EventsApiClient(getSplashApiClient());
